import { type BaseQueryMeta, createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiBlobRequest } from '#shared/api/requests';
import {
    OpenApiExportImportInProductListExampleMethod,
    type OpenApiExportImportInProductListExampleRequest,
    OpenApiExportImportInProductListExampleUrl,
    type OpenApiExportProductsRequest,
    type OpenApiExportProductsSearchParams,
    OpenApiExportProductsWithOversizeDimensionMethod,
    OpenApiExportProductsWithOversizeDimensionUrl,
    type OpenApiExportVariantsDefaultSizesAndWeightRequest,
    type OpenApiExportVariantsDefaultSizesAndWeightSearchParams,
    OpenApiExportVariantsWithLongNameMethod,
    OpenApiExportVariantsWithLongNameUrl,
    type OpenApiExportVariantsWithNoPriceRequest,
    type OpenApiExportVariantsWithNoPriceSearchParams,
    type OpenApiExportVariantsWithoutExternalIDsRequest,
} from '#shared/api/schema';
import { type DownloadableBlob, mapBlobResponse } from '#shared/lib/file';

const exportApi = createApi({
    name: 'entities/product/api/export',
    tagTypes: [],
    baseQuery: createBaseQuery(apiBlobRequest),
    endpoints: (builder) => {
        return {
            exportProducts: builder.query<
                DownloadableBlob,
                { queryParameters: OpenApiExportProductsSearchParams; body: OpenApiExportProductsRequest }
            >({
                query: ({ body, queryParameters }) => {
                    return {
                        url: '/products/export',
                        params: queryParameters,
                        method: 'POST',
                        body,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products.xlsx'),
            }),
            exportProductsWithDefaultSizesAndWeight: builder.query<
                DownloadableBlob,
                {
                    queryParameters: OpenApiExportVariantsDefaultSizesAndWeightSearchParams;
                    body: OpenApiExportVariantsDefaultSizesAndWeightRequest;
                }
            >({
                query: ({ body }) => {
                    return {
                        url: '/products/export/default-sizes-weights',
                        method: 'POST',
                        body,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products_default_sizes_and_weights.xlsx'),
            }),
            exportProductsWithoutPrice: builder.query<
                DownloadableBlob,
                {
                    queryParameters: OpenApiExportVariantsWithNoPriceSearchParams;
                    body: OpenApiExportVariantsWithNoPriceRequest;
                }
            >({
                query: ({ body }) => {
                    return {
                        url: '/products/export/no-price',
                        method: 'POST',
                        body,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products_without_price.xlsx'),
            }),
            exportProductsWithoutExternalIds: builder.query<
                DownloadableBlob,
                {
                    body: OpenApiExportVariantsWithoutExternalIDsRequest;
                }
            >({
                query: ({ body }) => {
                    return {
                        url: '/products/export/no-external-ids',
                        method: 'POST',
                        body,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products_without_external_ids.xlsx'),
            }),
            exportProductsWithOversizeDimension: builder.query<DownloadableBlob, void>({
                query: () => {
                    return {
                        url: OpenApiExportProductsWithOversizeDimensionUrl,
                        method: OpenApiExportProductsWithOversizeDimensionMethod,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products_oversize_dimension.xlsx'),
            }),
            exportProductsWithLongName: builder.query<DownloadableBlob, void>({
                query: () => {
                    return {
                        url: OpenApiExportVariantsWithLongNameUrl,
                        method: OpenApiExportVariantsWithLongNameMethod,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'products_with_long_name.xlsx'),
            }),
            exportProductListExample: builder.mutation<
                DownloadableBlob,
                OpenApiExportImportInProductListExampleRequest
            >({
                query: () => {
                    return {
                        url: OpenApiExportImportInProductListExampleUrl,
                        method: OpenApiExportImportInProductListExampleMethod,
                    };
                },
                transformResponse: (blobData: Blob, meta: BaseQueryMeta) =>
                    mapBlobResponse(blobData, meta, 'example.xlsx'),
            }),
        };
    },
});

export const useLazyExportProducts = exportApi.useLazyExportProductsQuery;
export const useLazyExportProductsWithDefaultSizesAndWeight =
    exportApi.useLazyExportProductsWithDefaultSizesAndWeightQuery;
export const useLazyExportProductsWithoutPrice = exportApi.useLazyExportProductsWithoutPriceQuery;
export const useLazyExportProductsWithoutExternalIds = exportApi.useLazyExportProductsWithoutExternalIdsQuery;
export const useLazyExportProductsWithOversizeDimension = exportApi.useLazyExportProductsWithOversizeDimensionQuery;
export const useLazyExportProductsWithLongName = exportApi.useLazyExportProductsWithLongNameQuery;
export const exportProductListExample = exportApi.endpoints.exportProductListExample.mutate;
